import { useClasses } from "../../hooks/useClasses";
import React, { useState, useEffect } from "react";
import { FiPlus, FiSearch, FiTrash2, FiExternalLink, FiLink } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";
import { format } from "date-fns";

const SUBJECTS = ["Mathematics","Science","English","Nepali","Social Studies","Computer","Health","Optional Math"];
const TYPES = [
  { value:"pdf",    label:"📄 PDF / Document",  hint:"Google Drive PDF" },
  { value:"doc",    label:"📝 Notes / Word",     hint:"Google Docs link" },
  { value:"video",  label:"🎥 Video",            hint:"YouTube link" },
  { value:"slides", label:"📊 Presentation",     hint:"Google Slides" },
  { value:"other",  label:"🔗 Other Link",       hint:"Any web link" },
];
const TYPE_COLOR = { pdf:"#dc2626", doc:"#2563eb", video:"#dc2626", slides:"#d97706", other:"#7c3aed" };
const TYPE_ICON  = { pdf:"📄", doc:"📝", video:"🎥", slides:"📊", other:"🔗" };

function isValidUrl(s) { try{new URL(s);return true;}catch{return false;} }

export default function TeacherMaterials() {
  const { userData } = useAuth();
  const { classes } = useClasses();
  const { getMaterials, addMaterial, deleteMaterial } = useData();
  const [mats, setMats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});
  const [form, setForm] = useState({ title:"", subject:"", classId:"", type:"pdf", description:"", linkUrl:"" });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const load = async () => { setLoading(true); const d = await getMaterials(); setMats(d); setLoading(false); };

  const validate = () => {
    const e = {};
    if (!form.title.trim())   e.title   = "Required";
    if (!form.subject)        e.subject  = "Required";
    if (!form.classId)        e.classId  = "Required";
    if (!form.linkUrl.trim()) e.linkUrl  = "Required";
    else if (!isValidUrl(form.linkUrl)) e.linkUrl = "Enter a valid URL";
    return e;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await addMaterial({ ...form, className: classes.find(x=>x.id===form.classId) ? `${classes.find(x=>x.id===form.classId).name} - ${classes.find(x=>x.id===form.classId).section}` : "", uploadedBy: userData?.name });
      toast.success("Material added!"); setShowAdd(false); setForm({ title:"", subject:"", classId:"", type:"pdf", description:"", linkUrl:"" }); setErrors({});
      load();
    } catch { toast.error("Failed to add"); }
    finally { setSaving(false); }
  };

  const handleDel = async (id) => {
    if (!window.confirm("Delete?")) return;
    await deleteMaterial(id); toast.success("Deleted!"); load();
  };

  const filtered = mats.filter(m => m.title.toLowerCase().includes(search.toLowerCase()) || m.subject.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fade-in">
      <div className="page-head">
        <div><h1 className="page-title">📖 Study Materials</h1><p className="page-subtitle">Share links with your students</p></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus size={15}/>Add Material</button>
      </div>
      <div style={{ marginBottom: 18 }}>
        <div className="search-box" style={{ maxWidth: 360 }}>
          <FiSearch className="search-icon" size={14}/>
          <input placeholder="Search materials…" value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
      </div>

      {loading ? <div className="loading-center"><div className="spinner"/></div> :
        <div className="g g3">
          {filtered.map(m => {
            const t = m.type || m.fileType || "other";
            const cl = TYPE_COLOR[t] || "#6b7280";
            return (
              <div key={m.id} className="card">
                <div style={{ height: 3, background: cl }}/>
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ display:"flex", gap: 10, alignItems:"flex-start", marginBottom: 10 }}>
                    <div style={{ width:40,height:40,borderRadius:"var(--r8)",background:cl+"15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.3rem",flexShrink:0 }}>
                      {TYPE_ICON[t]||"🔗"}
                    </div>
                    <div style={{ flex:1,minWidth:0 }}>
                      <h4 style={{ fontSize:".9rem",fontWeight:700,marginBottom:4 }}>{m.title}</h4>
                      <div style={{ display:"flex",gap:5 }}>
                        <span className="badge badge-blue" style={{fontSize:".67rem"}}>{m.subject}</span>
                        <span className="badge badge-gray" style={{fontSize:".67rem"}}>{m.className}</span>
                      </div>
                    </div>
                  </div>
                  {m.description && <p style={{fontSize:".8rem",color:"var(--gray-500)",marginBottom:10,lineHeight:1.5}}>{m.description}</p>}
                  <div style={{fontSize:".73rem",color:"var(--gray-400)",marginBottom:10}}>{m.uploadedAt?format(new Date(m.uploadedAt),"MMM d, yyyy"):""}</div>
                  <div style={{display:"flex",gap:8}}>
                    {(m.fileUrl||m.linkUrl) ? (
                      <a href={m.fileUrl||m.linkUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{flex:1,textDecoration:"none"}}>
                        <FiExternalLink size={12}/>Open
                      </a>
                    ) : <button className="btn btn-ghost btn-sm" disabled style={{flex:1}}>No link</button>}
                    <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDel(m.id)}>
                      <FiTrash2 size={12}/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="card" style={{gridColumn:"1/-1"}}><div className="empty-state"><div className="empty-icon">📖</div><div className="empty-title">No materials yet</div><div className="empty-desc">Add Google Drive or YouTube links for your students.</div></div></div>}
        </div>
      }

      <Modal show={showAdd} onClose={() => {setShowAdd(false);setErrors({});}} title="Add Study Material"
        footer={<><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving?<><div className="spinner spinner-sm"/>…</>:"Add Material"}</button></>}>
        <div className="form-group">
          <label className="form-label">Title *</label>
          <input className={`form-control${errors.title?" error":""}`} placeholder="e.g. Chapter 4 Notes" value={form.title} onChange={e=>{setForm(p=>({...p,title:e.target.value}));setErrors(p=>({...p,title:""}));}}/>
          {errors.title && <div className="form-error">{errors.title}</div>}
        </div>
        <div className="g g2">
          <div className="form-group">
            <label className="form-label">Subject *</label>
            <select className={`form-control${errors.subject?" error":""}`} value={form.subject} onChange={e=>{setForm(p=>({...p,subject:e.target.value}));setErrors(p=>({...p,subject:""}));}}>
              <option value="">Select</option>{SUBJECTS.map(s=><option key={s}>{s}</option>)}
            </select>
            {errors.subject && <div className="form-error">{errors.subject}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Class *</label>
            <select className={`form-control${errors.classId?" error":""}`} value={form.classId} onChange={e=>{setForm(p=>({...p,classId:e.target.value}));setErrors(p=>({...p,classId:""}));}}>
            </select>
            {errors.classId && <div className="form-error">{errors.classId}</div>}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Type</label>
          <select className="form-control" value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
            {TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><FiLink size={11}/>Share Link *</label>
          <input className={`form-control${errors.linkUrl?" error":""}`} placeholder={TYPES.find(t=>t.value===form.type)?.hint||"Paste link here"} value={form.linkUrl} onChange={e=>{setForm(p=>({...p,linkUrl:e.target.value}));setErrors(p=>({...p,linkUrl:""}));}}/>
          {errors.linkUrl && <div className="form-error">{errors.linkUrl}</div>}
          <div className="form-hint">Google Drive: Share → "Anyone with the link" → Copy link</div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-control" rows={2} value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Brief description (optional)"/>
        </div>
      </Modal>
    </div>
  );
}
